import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef
} from "react";
import fieldPropTypes from "./field-prop-types";
import { fieldValidation as validation } from "../utils/field-validation";
import { dataUrlToImage, createThumbnail } from "../utils/images";
import { range } from "../utils/fn";
import { useDialog } from "../context/messaging-layer";
import ValidationMessage from "../theme/default/validation-message";
import { SCREENS_WITH_MULTIPLE_PHOTO_PICKERS } from "../utils/constants";

const updateControlValidation = (field, appData, value) =>
  validation[field.type].update(field, appData, value);

const PhotoPicker = props => {
  const {
    field,
    appData,
    components: { Label, PhotoPreviewButton, Loader },
    screen
  } = props;
  const { name, title, data: fieldData } = field;
  const {
    data,
    lastNavAction,
    screenIndex,
    values,
    handleChange,
    nextScreen,
    resetFieldValidation,
    handleFileInputChange,
    settings,
    fieldValidation
  } = appData;

  const { showDialog } = useDialog();
  const inputFileRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const prefixName = useMemo(
    () =>
      SCREENS_WITH_MULTIPLE_PHOTO_PICKERS.includes(screen?.name)
        ? `_${name}`
        : "",
    [screen, name]
  );

  const imagesGroupName = useMemo(
    () =>
      SCREENS_WITH_MULTIPLE_PHOTO_PICKERS.includes(screen?.name)
        ? name
        : "attached_images",
    [screen, name]
  );

  const miniatureWidth = useMemo(
    () =>
      SCREENS_WITH_MULTIPLE_PHOTO_PICKERS.includes(screen?.name) ? 220 : 150,
    [screen, name]
  );

  const getPhotoPreview = useCallback(
    number => values[`__photo${prefixName}${number}:photoPreview`],
    [values, prefixName]
  );

  const getPhotoDescription = useCallback(
    number => values[`__photo${prefixName}${number}:photoName`],
    [values, prefixName]
  );

  const maxPhotos =
    fieldData?.photo_upload_limit || values.photo_upload_limit || 3;
  const photosTotal = range(1, maxPhotos, true).reduce(
    (total, n) => total + (getPhotoPreview(n) ? 1 : 0),
    0
  );

  const photoNumbers = range(
    1,
    Math.min((Math.floor(photosTotal / 3) + 1) * 3, maxPhotos),
    true
  );

  const openEditPhotoScreen = useCallback(
    (number, predefinedPhoto = {}) => {
      resetFieldValidation(name);
      let preselectedValues = null;

      if (
        (data.screens[screenIndex].params || "").includes(
          "TDScreenOptionFillAttachmentType"
        ) &&
        !values[`__photo${prefixName}${number}:attachment_type_cd`]
      ) {
        preselectedValues = {
          [`__photo${prefixName}${number}:attachment_type_cd`]:
            values[`__photo${prefixName}${number - 1}:attachment_type_cd`] ===
            "BEHAVHEALTH_CONSENTFORM"
              ? "BEHAVHEALTH_INTAKEFORM"
              : "BEHAVHEALTH_CONSENTFORM"
        };
      }

      nextScreen({
        data,
        screenIndex,
        screenParams: {
          originFieldName: `photo${prefixName}${number}`,
          imagesGroupName
        },
        formState: { ...values, ...preselectedValues, ...predefinedPhoto },
        action:
          ([
            "SharedImagesUpload",
            "CaseImagesUpload",
            "FileUploads",
            "InsuranceCapture",
            "ConsultationImagesPcp"
          ].includes(data.screens[screenIndex].name) ||
            (data.screens[screenIndex].params || "").includes(
              "TDScreenOptionUseDataModalScreen"
            )) &&
          data.screens[screenIndex].data
            ? {
                type: "modal_screen",
                value: data.screens[screenIndex].data?.modal_screen
              }
            : {
                type: "modal_screen",
                value: "ConsultationPhotoModal"
              }
      });
    },
    [data, screenIndex, values, prefixName]
  );

  const removePhoto = useCallback(number => {
    const fieldName = values[imagesGroupName]
      ? imagesGroupName
      : "files_upload_field";
    const attached_images = [...values[fieldName]];

    attached_images.splice(number - 1, 1);

    let newPreviewsAndNames = photoNumbers
      .filter(n => n !== number)
      .map((n, i) => ({
        [`__photo${prefixName}${i + 1}:photoPreview`]:
          values[`__photo${prefixName}${n}:photoPreview`],
        [`__photo${prefixName}${i + 1}:photoName`]:
          values[`__photo${prefixName}${n}:photoName`],
        [`__photo${prefixName}${i + 1}:attachment_type_cd`]:
          values[`__photo${prefixName}${n}:attachment_type_cd`]
      }));

    newPreviewsAndNames = Object.assign({}, ...newPreviewsAndNames, {
      [`__photo${prefixName}${newPreviewsAndNames.length + 1}:photoPreview`]:
        "",
      [`__photo${prefixName}${newPreviewsAndNames.length + 1}:photoName`]: "",
      [`__photo${prefixName}${
        newPreviewsAndNames.length + 1
      }:attachment_type_cd`]: ""
    });

    handleChange({
      ...newPreviewsAndNames,
      [fieldName]: attached_images
    });
  });

  const handlePhotoPickerClick = useCallback(
    number => {
      if (getPhotoPreview(number)) {
        const dialog = data.screens[screenIndex].dialogs?.find(
          d => d.name === "edit_photo"
        )?.fields;
        const dialogTitle = dialog?.find(f => f.name === "dialogTitle")?.title;
        const dialogEdit = dialog?.find(f => f.name === "edit")?.title;
        const dialogCancel = dialog?.find(f => f.name === "cancel")?.title;
        const dialogRemove = dialog?.find(f => f.name === "remove")?.title;
        showDialog({
          content: {
            title: dialog
              ? dialogTitle
              : settings.translations.what_do_you_want_to_do
          },
          actions: [
            {
              label: dialog ? dialogEdit : settings.translations.edit,
              name: "edit-dialog",
              dontCloseDialog: true,
              onClick: () => openEditPhotoScreen(number)
            },
            {
              label: dialog ? dialogRemove : settings.translations.remove,
              name: "remove-dialog",
              onClick: () => removePhoto(number)
            },
            {
              label: dialog ? dialogCancel : settings.translations.cancel,
              className: "secondary",
              name: "close-dialog"
            }
          ]
        });
      } else if (field.params.includes("TDFieldOptionSameWindowUploading")) {
        inputFileRef.current.click();
      } else {
        openEditPhotoScreen(number);
      }
    },
    [showDialog, openEditPhotoScreen, prefixName]
  );

  const onFileChangeHandler = useCallback(
    async (event, number) => {
      resetFieldValidation(name);
      if (name.includes(":")) {
        const prefix = name.split(":")[0];
        resetFieldValidation(`${prefix}:photoName`);
        resetFieldValidation(`${prefix}:attachment_type_cd`);
      }
      openEditPhotoScreen(
        number,
        await handleFileInputChange(
          event,
          `__photo${prefixName}${number}:photoPreview`
        )
      );
    },
    [handleFileInputChange, openEditPhotoScreen, prefixName]
  );

  useEffect(() => {
    // should not pass result to the useEffect() hook because it thinks it's a cleanup function and tries to call it
    updateControlValidation(field, appData, { fieldRef: wrapperRef });
  });
  const getFileItem = async (url, uuid) => {
    const image = await dataUrlToImage(url);

    const thumbnails = await Promise.all(
      [miniatureWidth, 500].map(size => createThumbnail(image, size, size))
    );
    return {
      thumbnails: { [miniatureWidth]: thumbnails[0], 500: thumbnails[1] },
      uuid
    };
  };

  const setAttachedImages = async uploadedPhoto => {
    const savedImages = [];
    const savedImagesPreview = {};

    for (let i = 0; i < maxPhotos; i++) {
      const prefixPhotoKey = `__photo${prefixName}${i + 1}`;
      const photoPreviewKey = `${prefixPhotoKey}:photoPreview`;
      const photoNameKey = `${prefixPhotoKey}:photoName`;

      if (uploadedPhoto[i]) {
        setIsLoading(true);
        const { url, uuid, description } = uploadedPhoto[i];

        /* eslint-disable no-await-in-loop */
        savedImagesPreview[photoPreviewKey] = await getFileItem(url, uuid);
        savedImagesPreview[photoNameKey] = description;
        savedImages.push(uploadedPhoto[i]);
      } else {
        savedImagesPreview[photoPreviewKey] = "";
        savedImagesPreview[photoNameKey] = "";
      }
    }

    handleChange({
      ...savedImagesPreview,
      [imagesGroupName]: savedImages
    });
    setIsLoading(false);
  };

  const cleanupUnsavedPhotos = () => {
    for (let i = 0; i < maxPhotos; i++) {
      const prefixPhotoKey = `__photo${prefixName}${i + 1}`;
      const photoPreviewKey = `${prefixPhotoKey}:photoPreview`;
      const photoNameKey = `${prefixPhotoKey}:photoName`;

      if (!values[photoPreviewKey] || !values[photoPreviewKey]?.uuid) {
        handleChange({
          [photoPreviewKey]: "",
          [photoNameKey]: ""
        });
      }
    }
  };
  useEffect(() => {
    if (field.data?.images && lastNavAction === "next") {
      setAttachedImages(field.data?.images);
    } else if (lastNavAction === "back") {
      cleanupUnsavedPhotos();
    }

    resetFieldValidation(name);
  }, []);

  const firstAvailableNumber =
    (values[imagesGroupName] ?? values.files_upload_field ?? []).length + 1;

  const invalid = fieldValidation[name] && !fieldValidation[name].isValid;
  const errorMessageEnabled =
    data.screens[screenIndex].name !== "VisitMhMinorConsent";
  const { errorMessage } = fieldValidation[name] || {};

  const { uploaded_photo, add_photo } = settings.translations;

  const renderPhotoPreviews = () =>
    photoNumbers.map((n, i) => {
      const minN = Math.min(n, firstAvailableNumber);
      const label = `${
        getPhotoPreview(minN)?.thumbnails?.[miniatureWidth]
          ? uploaded_photo
          : add_photo
      } ${i + 1}`;
      return (
        <div key={n}>
          <label
            htmlFor={`attach-photo-input-${n}`}
            style={{ display: "none" }}
          >
            {label}
          </label>
          <input
            type='file'
            ref={inputFileRef}
            accept='.jpg,.jpeg,.png,.gif,.pdf,.docx,.doc,.xlsx,.xls'
            style={{ display: "none" }}
            onChange={e => onFileChangeHandler(e, minN)}
            id={`attach-photo-input-${n}`}
            data-testid={`upload-file-input-${n}`}
          />
          <PhotoPreviewButton
            name={`photo${minN}`}
            index={++i}
            preview={getPhotoPreview(minN)?.thumbnails?.[miniatureWidth]}
            previewDescription={getPhotoDescription(minN)}
            invalid={!getPhotoPreview(minN) && invalid}
            onClick={() => handlePhotoPickerClick(minN)}
            photosTotal={photosTotal}
            label={label}
          />
        </div>
      );
    });

  return (
    <div className='photo-preview-wrapper' ref={wrapperRef}>
      <Label name={name} text={title} invalid={invalid} />
      {isLoading ? (
        <div className='loader-wrapper'>
          <Loader />
        </div>
      ) : (
        <div className='photo-preview-container'>{renderPhotoPreviews()}</div>
      )}
      <ValidationMessage isInvalid={invalid && errorMessageEnabled}>
        {errorMessage}
      </ValidationMessage>
    </div>
  );
};

PhotoPicker.propTypes = fieldPropTypes;

export default PhotoPicker;
